import React, { useMemo, useState } from "react";

import { Box, Button, CircularProgress, Stack, Typography } from "@mui/material";
import { IFileRepo, IK1Form } from "store/types";

import { K1EditValue } from "../hooks/use-k1-form";

import { K1FieldRow } from "./K1FieldRow";
import { K1InvestmentEntity } from "./K1InvestmentEntity";

interface Props {
  form: IK1Form;
  readOnly: boolean;
  isDirty: boolean;
  saving: boolean;
  fileData: IFileRepo | null;
  dealInvestors: { id: number; name: string; is_splittable?: boolean }[];
  investorsLoading?: boolean;
  getFieldValue: (key: string) => K1EditValue;
  setFieldValue: (key: string, value: K1EditValue) => void;
  isFieldEdited: (key: string) => boolean;
  onSave: () => void;
  onFileUpdated?: (file: IFileRepo) => void;
}

export const K1FormPanel: React.FC<Props> = ({
  form,
  readOnly,
  isDirty,
  saving,
  fileData,
  dealInvestors,
  investorsLoading,
  getFieldValue,
  setFieldValue,
  isFieldEdited,
  onSave,
  onFileUpdated
}) => {
  const [showAll, setShowAll] = useState(false);

  // A field counts as "extracted" when the extraction found a value for it (a boolean box is
  // extracted only when checked). Empty boxes ("") appear only in the "all fields" view. Filter on
  // the server value (not staged edits) so a row can't vanish mid-typing when the user clears it.
  const { visibleParts, extractedCount, totalCount } = useMemo(() => {
    let extracted = 0;
    let total = 0;
    const parts = form.parts
      .map((part) => {
        const groups = part.groups
          .map((group) => ({
            ...group,
            fields: group.fields.filter((f) => {
              total++;
              const hasValue = f.is_multiple
                ? Array.isArray(f.value) && f.value.length > 0
                : f.type === "boolean"
                  ? f.value === true
                  : String(f.value).trim() !== "";
              if (hasValue) extracted++;
              return showAll || hasValue;
            })
          }))
          .filter((group) => group.fields.length > 0);
        return { ...part, groups };
      })
      .filter((part) => part.groups.length > 0);
    return { visibleParts: parts, extractedCount: extracted, totalCount: total };
  }, [form, showAll]);

  return (
    <Stack sx={{ height: "100%", minHeight: 0 }}>
      {/* Banner */}
      <Stack
        direction="row"
        alignItems="center"
        gap="8px"
        sx={{
          px: "12px",
          py: "8px",
          bgcolor: "#effcfd",
          border: "1px solid rgba(38,166,174,0.4)",
          borderRadius: "8px",
          flexShrink: 0
        }}
      >
        <Box
          sx={{
            width: 26,
            height: 24,
            borderRadius: "4px",
            bgcolor: "#26a6ae",
            color: "white",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "9px",
            fontWeight: 700,
            flexShrink: 0
          }}
        >
          K-1
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: "12px", fontWeight: 500, color: "#1d878e", lineHeight: 1.3 }}>
            Schedule K-1 — Form 1065
          </Typography>
          <Typography sx={{ fontSize: "11px", color: "#496682", lineHeight: 1.3 }}>
            Extracted from document · review and correct values
          </Typography>
        </Box>
        <Box
          sx={{
            ml: "auto",
            fontSize: "11px",
            color: "#1d878e",
            bgcolor: "white",
            border: "1px solid rgba(38,166,174,0.4)",
            borderRadius: "32px",
            px: "8px",
            py: "2px",
            whiteSpace: "nowrap",
            flexShrink: 0
          }}
        >
          Tax year {form.tax_year}
        </Box>
      </Stack>

      {/* Investment Entity attribution */}
      {fileData && (
        <Box sx={{ mt: "12px", flexShrink: 0 }}>
          <K1InvestmentEntity
            fileData={fileData}
            dealInvestors={dealInvestors}
            investorsLoading={investorsLoading}
            readOnly={readOnly}
            onFileUpdated={onFileUpdated}
          />
        </Box>
      )}

      {/* Unsaved bar */}
      {!readOnly && isDirty && (
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ mt: "8px", px: "12px", py: "6px", bgcolor: "#68babf", borderRadius: "8px", flexShrink: 0 }}
        >
          <Typography sx={{ fontSize: "12px", color: "white" }}>Unsaved field edits</Typography>
          <Button
            onClick={onSave}
            disabled={saving}
            sx={{
              minWidth: 0,
              borderRadius: "32px",
              border: "1px solid white",
              color: "white",
              textTransform: "none",
              fontSize: "12px",
              fontWeight: 400,
              lineHeight: "16px",
              px: "8px",
              py: "2px",
              "&:hover": { bgcolor: "rgba(255,255,255,0.12)" }
            }}
          >
            {saving ? <CircularProgress size={14} sx={{ color: "white" }} /> : "Save"}
          </Button>
        </Stack>
      )}

      {/* Fields toolbar — extracted-only vs all-fields filter */}
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mt: "12px", flexShrink: 0 }}>
        <Typography sx={{ fontSize: "12px", color: "#496682" }}>
          {showAll ? "All fields" : "Extracted fields"} · {extractedCount} of {totalCount}
        </Typography>
        <Button
          variant="text"
          disableRipple
          onClick={() => setShowAll((v) => !v)}
          sx={{
            minWidth: 0,
            p: 0,
            textTransform: "none",
            fontSize: "12px",
            fontWeight: 400,
            color: "#26a6ae",
            "&:hover": { bgcolor: "transparent", textDecoration: "underline" }
          }}
        >
          {showAll ? `Show extracted only (${extractedCount})` : `Show all fields (${totalCount})`}
        </Button>
      </Stack>

      {/* Parts */}
      <Stack sx={{ flex: 1, minHeight: 0, overflowY: "auto", mt: "8px", gap: "20px" }}>
        {visibleParts.length === 0 && (
          <Typography sx={{ fontSize: "13px", color: "#92a7bc", textAlign: "center", py: "24px" }}>
            No values were extracted from this form. Use “Show all fields” to enter them manually.
          </Typography>
        )}
        {visibleParts.map((part) => (
          <Box key={part.part}>
            <Typography
              sx={{
                fontSize: "11px",
                fontWeight: 500,
                textTransform: "uppercase",
                letterSpacing: "0.08em",
                color: "#496682",
                mb: "6px"
              }}
            >
              {part.title}
            </Typography>
            <Stack sx={{ border: "1px solid #dbe8f5", borderRadius: "8px", overflow: "hidden" }}>
              {part.groups.map((group, gi) => (
                <Stack
                  key={`${group.category_number}-${gi}`}
                  direction="row"
                  sx={{ borderTop: gi === 0 ? "none" : "1px solid #dbe8f5", bgcolor: "#f6f8fc" }}
                >
                  <Box
                    sx={{
                      width: 34,
                      flexShrink: 0,
                      pt: "9px",
                      textAlign: "center",
                      fontSize: "12px",
                      fontWeight: 700,
                      color: "#071f35",
                      borderRight: "1px solid #dbe8f5"
                    }}
                  >
                    {group.category_number}
                  </Box>
                  <Stack sx={{ flex: 1, minWidth: 0 }}>
                    {group.fields.map((field) => (
                      <K1FieldRow
                        key={field.key}
                        field={field}
                        value={getFieldValue(field.key)}
                        edited={field.is_manually_changed || isFieldEdited(field.key)}
                        readOnly={readOnly}
                        onChange={setFieldValue}
                      />
                    ))}
                  </Stack>
                </Stack>
              ))}
            </Stack>
          </Box>
        ))}
      </Stack>
    </Stack>
  );
};
